import React, { useState, useEffect } from 'react'
import api from '../../services/api'
import { useAuth } from '../../contexts/AuthContext'
import { ENTITY_TYPES } from '../../config/entityFormConfig'
import Icon from '../../components/Icon'
import { FormGroup, Select, Input, Button } from '../../components/ui'
import { translateApiError } from '../../utils/errorMessages'
import './Admin.css'

const ENTITY_LABELS = {
  clients: 'Клиенты',
  contacts: 'Контакты',
  objects: 'Объекты',
  contracts: 'Договоры',
  stages: 'Стадии',
  acts: 'Акты',
}

const REFERENCE_TYPE_NAMES = {
  клиент_статус: 'Статусы клиентов',
  клиент_город: 'Города клиентов',
  подразделение: 'Подразделения',
  тип_расходов: 'Типы расходов',
  категория_расходов: 'Категории расходов',
  типы_распределения: 'Типы распределения',
  статус_договора: 'Статусы договоров',
  квартал: 'Кварталы',
  период: 'Периоды',
}

const FIELD_TYPE_LABELS = {
  string: 'Строка',
  number: 'Число',
  boolean: 'Да/Нет',
  reference: 'Справочник',
}

function CustomFields() {
  const { user } = useAuth()
  const canManage = user?.permissions?.['custom_fields.manage']
  const [entityType, setEntityType] = useState('clients')
  const [definitions, setDefinitions] = useState([])
  const [references, setReferences] = useState({})
  const [customReferences, setCustomReferences] = useState([])
  const [loading, setLoading] = useState(true)
  const [editingId, setEditingId] = useState(null)
  const [formData, setFormData] = useState({ code: '', label: '', field_type: 'string', sort_order: 0, is_required: false, reference_type: '', custom_reference_id: '', options: [] })
  const [createRefModal, setCreateRefModal] = useState(false)

  useEffect(() => {
    fetchDefinitions()
  }, [entityType])

  useEffect(() => {
    api.get('/admin/reference').then((r) => {
      setReferences(r.data?.references || {})
      setCustomReferences(r.data?.custom_references || [])
    }).catch(() => {
      setReferences({})
      setCustomReferences([])
    })
  }, [])

  const fetchDefinitions = async () => {
    try {
      setLoading(true)
      const res = await api.get(`/admin/custom-fields/definitions?entity_type=${entityType}`)
      setDefinitions(res.data?.definitions || [])
    } catch (e) {
      console.error(e)
      setDefinitions([])
    } finally {
      setLoading(false)
    }
  }

  const getReferenceSelectValue = () => {
    if (formData.custom_reference_id) return `custom_${formData.custom_reference_id}`
    return formData.reference_type || ''
  }

  const setReferenceSelectValue = (val) => {
    if (val === 'new') {
      setCreateRefModal(true)
      return
    }
    if (val && val.startsWith('custom_')) {
      setFormData((p) => ({ ...p, custom_reference_id: val.replace('custom_', ''), reference_type: '' }))
    } else {
      setFormData((p) => ({ ...p, reference_type: val || '', custom_reference_id: '' }))
    }
  }

  const handleSaveDefinition = async (e) => {
    e.preventDefault()
    if (!canManage) return
    try {
      const payload = {
        label: formData.label,
        code: formData.code,
        field_type: formData.field_type,
        sort_order: formData.sort_order,
        is_required: formData.is_required,
      }
      if (formData.field_type === 'reference') {
        payload.reference_type = formData.custom_reference_id ? null : (formData.reference_type || null)
        payload.custom_reference_id = formData.custom_reference_id || null
      }
      if (editingId) {
        await api.put(`/admin/custom-fields/definitions/${editingId}`, payload)
      } else {
        await api.post('/admin/custom-fields/definitions', {
          entity_type: entityType,
          ...payload,
        })
      }
      setEditingId(null)
      setFormData({ code: '', label: '', field_type: 'string', sort_order: 0, is_required: false, reference_type: '', custom_reference_id: '', options: [] })
      fetchDefinitions()
    } catch (err) {
      const d = err.response?.data
      alert(translateApiError(d?.message || d?.error || d?.details || err.message))
    }
  }

  const handleDeleteDefinition = async (id) => {
    if (!canManage) return
    try {
      await api.delete(`/admin/custom-fields/definitions/${id}`)
      fetchDefinitions()
      if (editingId === id) setEditingId(null)
    } catch (err) {
      const data = err.response?.data
      if (data?.error === 'field_has_data' && data?.usage_count) {
        if (window.confirm(`${data.message}\n\nПродолжить удаление?`)) {
          try {
            await api.delete(`/admin/custom-fields/definitions/${id}`, { data: { confirm_data_loss: true } })
            fetchDefinitions()
            if (editingId === id) setEditingId(null)
          } catch (e2) {
            const d = e2.response?.data
            alert(translateApiError(d?.message || d?.error || e2.message))
          }
        }
      } else {
        alert(translateApiError(data?.message || data?.error || err.message))
      }
    }
  }

  const handleCreateReference = async (name, values) => {
    try {
      const res = await api.post('/admin/custom-references', { name, values: values || [] })
      const created = res.data?.custom_reference
      if (created) {
        setFormData((p) => ({ ...p, custom_reference_id: created.id, reference_type: '' }))
        setCustomReferences((prev) => [...prev, created])
      }
      setCreateRefModal(false)
    } catch (err) {
      const d = err.response?.data
      alert(d?.message || d?.error || err.message)
    }
  }

  const referenceOptions = [
    ...Object.keys(references).sort().map((t) => ({
      value: t,
      label: REFERENCE_TYPE_NAMES[t] || t,
    })),
    ...customReferences.map((r) => ({
      value: `custom_${r.id}`,
      label: r.name,
    })),
  ]

  const getReferenceLabel = (d) => {
    if (d.reference_type) return REFERENCE_TYPE_NAMES[d.reference_type] || d.reference_type
    if (d.custom_reference_id) {
      const cr = customReferences.find((r) => r.id === d.custom_reference_id)
      return cr?.name || d.custom_reference_id
    }
    return null
  }

  return (
    <div className="admin-page">
      <p style={{ marginBottom: '1rem', color: 'var(--color-text-secondary)' }}>
        Настройте дополнительные поля для сущностей. Они отображаются в формах создания и редактирования и на карточках.
      </p>

      <div style={{ marginBottom: '1.5rem', maxWidth: '320px' }}>
        <FormGroup label="Сущность">
          <Select
            value={entityType}
            onChange={(e) => setEntityType(e.target.value)}
            aria-label="Сущность"
          >
            {ENTITY_TYPES.map((t) => (
              <option key={t} value={t}>{ENTITY_LABELS[t] || t}</option>
            ))}
          </Select>
        </FormGroup>
      </div>

      {canManage && (
        <div className="admin-field-form">
          <form onSubmit={handleSaveDefinition}>
            <h3>{editingId ? 'Редактировать поле' : 'Добавить поле'}</h3>
            <div className="admin-field-form-grid">
              <FormGroup label="Код (латиница, без пробелов)">
                <Input
                  type="text"
                  value={formData.code}
                  onChange={(e) => setFormData((p) => ({ ...p, code: e.target.value.replace(/\s/g, '_') }))}
                  required
                  placeholder="например source"
                  disabled={!!editingId}
                />
              </FormGroup>
              <FormGroup label="Название">
                <Input
                  type="text"
                  value={formData.label}
                  onChange={(e) => setFormData((p) => ({ ...p, label: e.target.value }))}
                  required
                  placeholder="например Источник"
                />
              </FormGroup>
              <FormGroup label="Тип">
                <Select
                  value={formData.field_type}
                  onChange={(e) => setFormData((p) => ({ ...p, field_type: e.target.value }))}
                  aria-label="Тип поля"
                >
                  {Object.entries(FIELD_TYPE_LABELS).map(([k, v]) => (
                    <option key={k} value={k}>{v}</option>
                  ))}
                </Select>
              </FormGroup>
              {formData.field_type === 'reference' && (
                <FormGroup label="Справочник">
                  <Select
                    value={getReferenceSelectValue()}
                    onChange={(e) => setReferenceSelectValue(e.target.value)}
                    aria-label="Справочник"
                  >
                    <option value="">— Выберите —</option>
                    {referenceOptions.map((o) => (
                      <option key={o.value} value={o.value}>{o.label}</option>
                    ))}
                    <option value="new">+ Создать новый справочник</option>
                  </Select>
                </FormGroup>
              )}
              <FormGroup label="Порядок">
                <Input
                  type="number"
                  value={formData.sort_order}
                  onChange={(e) => setFormData((p) => ({ ...p, sort_order: parseInt(e.target.value, 10) || 0 }))}
                />
              </FormGroup>
              <FormGroup label="">
                <div className="checkbox-group" style={{ display: 'flex', alignItems: 'center', gap: 'var(--spacing-sm)' }}>
                  <input
                    type="checkbox"
                    id="is_required"
                    checked={formData.is_required}
                    onChange={(e) => setFormData((p) => ({ ...p, is_required: e.target.checked }))}
                  />
                  <label htmlFor="is_required" style={{ margin: 0 }}>Обязательное</label>
                </div>
              </FormGroup>
            </div>
            <div className="form-actions" style={{ marginTop: 'var(--spacing-md)' }}>
              {editingId && (
                <Button type="button" variant="secondary" onClick={() => setEditingId(null)}>
                  Отмена
                </Button>
              )}
              <Button type="submit" variant="primary">
                {editingId ? 'Сохранить' : 'Добавить'}
              </Button>
            </div>
          </form>
        </div>
      )}

      <div className="admin-table-wrap">
        <h3 className="admin-section-title">Поля для «{ENTITY_LABELS[entityType] || entityType}»</h3>
        {loading ? (
          <div className="loading">Загрузка...</div>
        ) : definitions.length === 0 ? (
          <p className="info-note">Нет дополнительных полей. Добавьте поле выше.</p>
        ) : (
          <div className="admin-table-frame">
          <div className="table-responsive">
          <table className="ui-table data-table">
            <thead>
              <tr>
                <th>Код</th>
                <th>Название</th>
                <th>Тип</th>
                <th>Обязательное</th>
                <th>Порядок</th>
                {canManage && <th>Действия</th>}
              </tr>
            </thead>
            <tbody>
              {definitions.map((d) => (
                <tr key={d.id}>
                  <td><code>{d.code}</code></td>
                  <td>{d.label}</td>
                  <td>
                    {FIELD_TYPE_LABELS[d.field_type] || d.field_type}
                    {d.field_type === 'reference' && (d.reference_type || d.custom_reference_id) && ` (${getReferenceLabel(d)})`}
                  </td>
                  <td>{d.is_required ? 'Да' : 'Нет'}</td>
                  <td>{d.sort_order}</td>
                  {canManage && (
                    <td>
                      <button
                        type="button"
                        className="btn-icon"
                        onClick={() => {
                          setEditingId(d.id)
                          setFormData({
                            code: d.code,
                            label: d.label,
                            field_type: d.field_type,
                            sort_order: d.sort_order || 0,
                            is_required: !!d.is_required,
                            reference_type: d.reference_type || '',
                            custom_reference_id: d.custom_reference_id || '',
                            options: d.options || [],
                          })
                        }}
                        title="Редактировать"
                      >
                        <Icon name="edit" size={18} />
                      </button>
                      <button type="button" className="btn-icon btn-danger" onClick={() => handleDeleteDefinition(d.id)} title="Удалить">
                        <Icon name="trash" size={18} />
                      </button>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
          </div>
          </div>
        )}
      </div>

      {createRefModal && (
        <CreateReferenceModal
          onSave={handleCreateReference}
          onClose={() => setCreateRefModal(false)}
        />
      )}
    </div>
  )
}

function CreateReferenceModal({ onSave, onClose }) {
  const [name, setName] = useState('')
  const [values, setValues] = useState([{ value: '', label: '' }])

  const addRow = () => setValues((v) => [...v, { value: '', label: '' }])
  const removeRow = (i) => setValues((v) => v.filter((_, j) => j !== i))
  const updateRow = (i, key, val) => setValues((v) => v.map((row, j) => (j === i ? { ...row, [key]: val } : row)))

  const handleSubmit = (e) => {
    e.preventDefault()
    const vs = values.filter((r) => (r.value || '').trim())
    onSave(name.trim(), vs)
  }

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '500px', maxHeight: '90vh', overflow: 'auto' }}>
        <h3>Создать справочник</h3>
        <form onSubmit={handleSubmit}>
          <FormGroup label="Название">
            <Input type="text" value={name} onChange={(e) => setName(e.target.value)} required placeholder="например Источники" />
          </FormGroup>
          <div className="ui-form-group" style={{ overflow: 'hidden' }}>
            <span className="ui-form-group-label">Значения</span>
            {values.map((row, i) => (
              <div key={i} style={{ display: 'flex', gap: '8px', marginBottom: '8px', alignItems: 'center', minWidth: 0 }}>
                <Input
                  type="text"
                  placeholder="Значение"
                  value={row.value}
                  onChange={(e) => updateRow(i, 'value', e.target.value)}
                  style={{ flex: '1 1 0', minWidth: 0 }}
                />
                <Input
                  type="text"
                  placeholder="Подпись"
                  value={row.label}
                  onChange={(e) => updateRow(i, 'label', e.target.value)}
                  style={{ flex: '1 1 0', minWidth: 0 }}
                />
                <button type="button" className="btn-icon btn-danger" onClick={() => removeRow(i)} style={{ flexShrink: 0 }} title="Удалить">
                  <Icon name="trash" size={18} />
                </button>
              </div>
            ))}
            <Button type="button" variant="secondary" onClick={addRow}>+ Добавить</Button>
          </div>
          <div className="form-actions">
            <Button type="button" variant="secondary" onClick={onClose}>Отмена</Button>
            <Button type="submit" variant="primary">Создать</Button>
          </div>
        </form>
      </div>
    </div>
  )
}

export default CustomFields
